import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const TIER_COLORS = {
  cold: '#6366f1', warm: '#f59e0b', hot: '#ef4444', sales_ready: '#10b981',
};

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: 'var(--card-bg)', borderRadius: 10, padding: '20px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 400, fontFamily: 'var(--vela-font-num)', letterSpacing: '-0.02em', color: color || 'var(--vela-text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats]       = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/dashboard/stats'),
      axios.get('/api/dashboard/activity'),
    ]).then(([s, a]) => {
      setStats(s.data);
      setActivity(a.data);
    }).catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>
      Loading your dashboard...
    </div>
  );

  if (error || !stats) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)' }}>
      Couldn't load your dashboard. Please refresh the page.
    </div>
  );

  const tierData = [
    { name: 'Cold',        value: stats.tiers.cold,        color: TIER_COLORS.cold },
    { name: 'Warm',        value: stats.tiers.warm,        color: TIER_COLORS.warm },
    { name: 'Hot',         value: stats.tiers.hot,         color: TIER_COLORS.hot },
    { name: 'Sales Ready', value: stats.tiers.sales_ready, color: TIER_COLORS.sales_ready },
  ];

  const weekDelta = stats.weekComparison.this_week - stats.weekComparison.last_week;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Overview</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Your lead pipeline at a glance
        </p>
      </div>

      {/* Top stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard label="Total Leads"    value={stats.totals.total || 0} />
        <StatCard label="Added This Week" value={stats.weekComparison.this_week || 0}
          sub={weekDelta >= 0 ? `+${weekDelta} vs last week` : `${weekDelta} vs last week`}
          color={weekDelta >= 0 ? 'var(--success)' : 'var(--danger)'} />
        <StatCard label="Email Open Rate"  value={`${stats.emailStats.open_rate || 0}%`}
          sub={`${stats.emailStats.opens || 0} of ${stats.emailStats.sent || 0} sent`} />
        <StatCard label="Click Rate"       value={`${stats.emailStats.click_rate || 0}%`} />
        <StatCard label="Avg Days to Ready" value={`${stats.avgDaysToSalesReady || '—'}`}
          sub="from lead entry to sales-ready" />
        <StatCard label="CRM Handoffs (Month)" value={stats.crmHandoffs.count || 0}
          sub={stats.crmHandoffs.revenue > 0 ? `$${stats.crmHandoffs.revenue.toLocaleString()} attributed` : 'No revenue tracked yet'}
          color="var(--success)" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>

        {/* Tier breakdown pie */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Leads by Tier</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={tierData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {tierData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tier bar chart */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Active Leads by Stage</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierData} barCategoryGap="35%">
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {tierData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top emails + activity feed */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20 }}>

        {/* Top emails */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Top Performing Emails</h3>
          {!stats?.topEmails?.length && (
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No email data yet. Leads will start receiving sequences once enrolled.</p>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Subject', 'Sequence', 'Sent', 'Opens', 'Clicks'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0 8px 8px 0', color: 'var(--text-muted)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(stats?.topEmails || []).map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 8px 8px 0', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</td>
                  <td style={{ padding: '8px 8px 8px 0' }}>
                    <span style={{
                      background: TIER_COLORS[e.tier] + '22',
                      color: TIER_COLORS[e.tier],
                      borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 600,
                    }}>{e.tier}</span>
                  </td>
                  <td style={{ padding: '8px 8px 8px 0', color: 'var(--text-muted)' }}>{e.sent}</td>
                  <td style={{ padding: '8px 8px 8px 0', color: 'var(--success)', fontWeight: 600 }}>{e.opens}</td>
                  <td style={{ padding: '8px 8px 8px 0', color: 'var(--primary)', fontWeight: 600 }}>{e.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Activity feed */}
        <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Recent Activity</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!activity.length && (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No activity yet.</p>
            )}
            {activity.slice(0, 8).map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: a.points > 0 ? 'var(--primary-light)' : 'rgba(220,38,38,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14,
                }}>
                  {a.points > 0 ? '↑' : '↓'}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {a.first_name || ''} {a.last_name || a.email}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {a.event_type.replace(/_/g, ' ')} · Score: {a.score_after}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
